import * as React from "react";
import { DataGridPro, GridFilterPanel, GridToolbarContainer, GridToolbarExport, useGridApiRef } from "@mui/x-data-grid-pro";
import Tooltip from '@mui/material/Tooltip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Stack, styled, TextField } from "@mui/material";

export const FilterChip = styled(Box)(({ theme }) => ({
  backgroundColor: "#eceffaff",
  height: "28px",
  color: "#1e40af",
  padding: "4px 10px 4px 16px",
  borderRadius: "12px",
  fontSize: "12px",
  marginRight: "6px",
  marginBottom: "4px",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
}));

export const StyledTextSearch = styled(TextField)(({ theme }) => ({
  width: '350px',
  backgroundColor: '#eceffaff',
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "#e0e7ff",
    },
    "&:hover fieldset": {
      borderColor: "#1e40af",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1e40af",
    },
  },
}));

const StyledDataGrid = styled(DataGridPro)((props) => ({
  width: props.width || "fit-content",
  minWidth: "50px",
  WebkitFontSmoothing: "auto",
  letterSpacing: "normal",
  fontFamily: "Lato",
  fontSize: "12px",
  margin: "0 auto",
  "& .MuiDataGrid-iconSeparator": {
    color: "rgba(92, 6, 140, 1)",
  },
  "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
  },
  "& .MuiDataGrid-cell": {
    color: "rgba(0,0,0,.85)",
    display: "flex",
    alignItems: "center",
  },

  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
  {
    outline: "none",
  },
  "& .MuiPaginationItem-root": {
    borderRadius: 0,
  },
  "& .MuiDataGrid-main": {
    overflow: props.overflow || "unset",
  },
  "& .MuiDataGrid-columnHeaders": {
    position: "sticky",
    top: props.top || 36,
    zIndex: 100,
    backgroundColor: "#63cae1",
    color: "#fff",
    fontSize: "13px",
    fontWeight: 600
  },
  "& .MuiDataGrid-columnHeaderRow": {
    backgroundColor: "#63cae1",
  },
  "& .MuiDataGrid-virtualScroller": {
    marginTop: "0!important",
    minHeight: "100px",
    maxHeight: props.height || "auto",
    overflowY: 'auto !important',
    // maxHeight: "500px"
  },
  "& .link": {
    color: "#5C068C",
    textDecoration: "none",
    cursor: "pointer"
  },
  "& .status": {
    padding: "5px 12px",
    borderRadius: "20px",
    color: "#fff",
  },
  "& .status.closed-status": {
    backgroundColor: "#00168999",
  },
  "& .status.open-status": {
    color: "#5C0F8B",
    backgroundColor: "#63CAE1",
  },
  "& .status.research-status": {
    backgroundColor: "#8A1A9B",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    lineHeight: "20px",
    textWrap: "wrap",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#63cae1",
  },
}));

const StyledStack = styled(Stack)((props) => ({
  letterSpacing: "normal",
  fontFamily: "Lato",
  fontSize: "20px",
  color: "#5c088c",
}));

const StyledGrid = (props) => {
  const {
    data,
    columns,
    width,
    height,
    overflow,
    top,
    rowHeight,
    withFilter,
    withMenu,
    paginationModel,
    setPaginationModel,
    onFilterModelChange,
    onSortingModelChange,
    sortModel,
    onRowClick,
    rowCount,
    withCheckboxSelection,
    selectionModel,
    onRowSelectionModelChange,
    columnVisibilityModel,
    hideFooterSelectedRowCount = true,
    processRowUpdate,
    isCellEditable,
    withExport,
    autoFocusEdit // optional: { field: 'appliedAmount', trigger: number }
  } = props;

  const CustomToolbar = React.useCallback(() => {
    if (!withExport) return null;
    return (
      <GridToolbarContainer sx={{ gap: 1 }}>
        <Tooltip title="Export / Print">
          <span>
            <GridToolbarExport
              csvOptions={{ fileName: 'export' }}
              printOptions={{ hideFooter: false, hideToolbar: false }}
            />
          </span>
        </Tooltip>
        {/* Optional: separate quick access buttons */}
        <Tooltip title="Download CSV">
          <span onClick={() => {
            // trigger built-in csv export via button simulation
            const btn = document.querySelector('[data-testid="ToolbarExport"] button');
            btn && btn.click();
          }} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
            <FileDownloadIcon fontSize="small" />
          </span>
        </Tooltip>
        <Tooltip title="Print">
          <span onClick={() => {
            // open print menu then trigger print option
            const btn = document.querySelector('[data-testid="ToolbarExport"] button');
            if (btn) {
              btn.click();
              // allow menu to render then find Print option
              setTimeout(() => {
                const items = Array.from(document.querySelectorAll('li[role="menuitem"]')); // menu items
                const print = items.find(i => /print/i.test(i.textContent || ''));
                print && print.click();
              }, 50);
            }
          }} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
            <PrintIcon fontSize="small" />
          </span>
        </Tooltip>
      </GridToolbarContainer>
    );
  }, [withExport]);

  // Ensure rows is always an array to avoid DataGrid internal errors
  const rows = Array.isArray(data) ? data : [];

  const apiRef = useGridApiRef();

  // Effect to start edit mode on the first selected row when autoFocusEdit requested.
  React.useEffect(() => {
    if (!autoFocusEdit || !selectionModel || !selectionModel.length) return;
    // Only trigger when selection count matches the trigger (default 1)
    const triggerCount = typeof autoFocusEdit.trigger === 'number' ? autoFocusEdit.trigger : 1;
    if (selectionModel.length !== triggerCount) return;
    const rowId = selectionModel[selectionModel.length - 1];
    // Delay to ensure grid internal selection finished
    setTimeout(() => {
      try {
        if (!apiRef.current) return;
        const field = autoFocusEdit.field;
        if (apiRef.current.startCellEditMode) {
          apiRef.current.startCellEditMode({ id: rowId, field });
          return;
        }
        if (apiRef.current.setCellMode) {
          apiRef.current.setCellMode(rowId, field, 'edit');
          return;
        }
        if (apiRef.current.setCellFocus) {
          apiRef.current.setCellFocus(rowId, field);
        }
      } catch {/* silent */}
    }, 30);
  }, [selectionModel, autoFocusEdit]);

  return (
  <StyledDataGrid
      width={width}
      height={height}
      overflow={overflow}
      top={top}
    rowHeight={rowHeight}
      rows={rows}
      columns={columns}
      apiRef={apiRef}
      showToolbar
      disableSelectionOnClick
      disableRowSelectionOnClick
      disableColumnFilter={withFilter ? false : true}
      disableColumnMenu={withMenu ? false : true}
      checkboxSelection={withCheckboxSelection ? true : false}
      processRowUpdate={processRowUpdate}
      {...(onFilterModelChange
        ? { onFilterModelChange: onFilterModelChange }
        : {})}
      {...(onSortingModelChange
        ? { onSortModelChange: onSortingModelChange }
        : {})}
      disableColumnSelector
      onRowClick={onRowClick}
      pagination
      initialState={{
        pagination: { paginationModel: { pageSize: paginationModel?.pageSize || 50 } },
        columns: { columnVisibilityModel: columnVisibilityModel || {} },
      }}
      pageSizeOptions={[10, 20, 50]}
      paginationModel={paginationModel}
      paginationMode={paginationModel ? "server" : "client"}
      rowCount={rowCount}
      onPaginationModelChange={setPaginationModel}
      isCellEditable={isCellEditable || (() => false)}
      autoHeight={height}
      filterMode="server"
      sortingMode={onSortingModelChange ? "server" : 'client'}
      sortingOrder={['asc', 'desc']}
      {...(sortModel ? { sortModel } : {})}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      hideFooterSelectedRowCount={hideFooterSelectedRowCount}
      keepNonExistentRowsSelected
      slots={{
        toolbar: withExport ? CustomToolbar : undefined,
      }}
      localeText={{
        footerRowSelected: (count) => `${count} selected`,
      }}
      components={{
        FilterPanel: (props) => (
          <div style={{ minWidth: "650px" }}>
            <GridFilterPanel {...props} />
          </div>
        ),
        NoRowsOverlay: () => (
          <StyledStack
            height="100%"
            alignItems="center"
            justifyContent="center"
          >
            No data to show
          </StyledStack>
        ),
        NoResultsOverlay: () => (
          <StyledStack
            height="100%"
            alignItems="center"
            justifyContent="center"
          >
            No results
          </StyledStack>
        ),
      }}
    />
  );
};

export default StyledGrid;
